// src/components/AssessmentQuiz.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import translations from './translations';
import './AssessmentQuiz.css';
import { STORAGE_KEYS } from '../config';
import { safeGet, safeSet, safeRemove } from '../utils/storage';
import { getCommonParams } from '../utils/url';
import { resolveSubmissionContext } from '../utils/submissionRecovery';
import {
  flushAnswerCheckpoint,
  flushCheckpointOnPageHide,
  queueAnswerCheckpoint,
  queueAnswerSnapshot,
  fetchCheckpointAnswers,
} from '../utils/checkpoint';
import { getAssessmentConfig } from '../data/registry';
import { initStartTime, computeAndSaveTimeTaken } from '../utils/timing';
import { addMetadataToParams, isAdultAssessment } from '../utils/assessmentLanguage';
import { loadAdultQuestionBank, bankAnswerCacheKey, bankOrderCacheKey, restoreAdultAnswers } from '../utils/adultQuestionBank';


// 工具函數：打亂題目順序
// const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

function AssessmentQuiz() {
  const navigate = useNavigate();

  // 從 URL 參數獲取語言，默認為 zh-CN
  const getContext = () => resolveSubmissionContext({
    userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}), urlParams: getCommonParams(),
    activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
    sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}),
  });
  const defaultLang = getContext().defaultLang;
  
  const t = translations[defaultLang] || translations['zh-CN'];

  // 獲取當前測評類型，默認為 adult14_hr
  const assessmentType = safeGet(STORAGE_KEYS.ASSESSMENT_TYPE, 'adult14_hr');
  const ageGroup = safeGet(STORAGE_KEYS.USER_INFO, {}).ageGroup || '';
  
  const [quizData, setQuizData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bank, setBank] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  // 動態加載題目數據
  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      setLoading(true);
      setLoadError(false);
      const config = getAssessmentConfig(assessmentType);
      try {
        if (isAdultAssessment(assessmentType)) {
          // The server may already have pinned a release on an older link.
          let serverAnswers = [];
          try { serverAnswers = await fetchCheckpointAnswers(getContext()); }
          catch (error) { if (error.response) throw error; }
          const release = await loadAdultQuestionBank(getContext());
          if (cancelled) return;
          const context = getContext();
          setAnswers(restoreAdultAnswers(context, serverAnswers, release.questions.map(question => question.id)));
          setBank(release);
          setQuizData(release.questions);
        } else {
          const data = await config.loadData(defaultLang, ageGroup);
          if (!cancelled) { setBank(null); setQuizData(data || []); }
        }
      } catch (error) {
        console.warn('Failed to load quiz data:', error.message);
        if (!cancelled) { setQuizData([]); setLoadError(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadData();
    return () => { cancelled = true; };
  // The context is read again after server metadata is restored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLang, assessmentType, ageGroup, reloadCount]);

  // 使用狀態管理題目、索引、答案和提交狀態
  const [shuffledQuizData, setShuffledQuizData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 初始化 quizAnswers，防止 localStorage 解析錯誤
  const [answers, setAnswers] = useState(() => {
    return safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
  });

  useEffect(() => {
    if (!isAdultAssessment(assessmentType) || bank) void flushAnswerCheckpoint();
    const handlePageHide = () => { void flushCheckpointOnPageHide(); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') handlePageHide();
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [assessmentType, bank]);



  useEffect(() => {
    // ✅ 使用工具自動處理時間格式正確性
    initStartTime();

    if (quizData && quizData.length > 0) {
      const all = isAdultAssessment(assessmentType)
        ? quizData : quizData.map((q, i) => ({ ...q, id: i + 1 }));
      const byId = new Map(all.map(q => [q.id, q]));
      // 使用特定於語言和測評類型的緩存鍵
      const ageGroupSuffix = ageGroup ? `_${ageGroup}` : '';
      const key = bank ? bankOrderCacheKey(getContext())
        : `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${assessmentType}${ageGroupSuffix}_${defaultLang}`;
      
      // 嘗試獲取現有順序
      let order = safeGet(key, []);
      
      // 如果沒有找到特定類型的順序，嘗試舊的鍵名（為了兼容性）
      if (!order || order.length === 0) {
         const oldKey = `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${defaultLang}`;
         const oldOrder = safeGet(oldKey, []);
         // 只有當是 adult14_hr 且有舊數據時才使用舊數據
         if (assessmentType === 'adult14_hr' && oldOrder && oldOrder.length > 0) {
            order = oldOrder;
         }
      }

      const valid = new Set(['0','1','2']);
      const polluted = Object.values(answers || {}).some(v => !valid.has(String(v)));
      
      if (polluted) {
        safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
        if (bank) safeRemove(bankAnswerCacheKey(getContext()));
        safeRemove(key);
        order = all.map(q => q.id).sort(() => Math.random() - 0.5);
        safeSet(key, order);
        setShuffledQuizData(order.map(id => byId.get(id)));
        setCurrentIndex(0);
        return;
      }
      
      if (!Array.isArray(order) || order.length !== all.length || new Set(order).size !== all.length ||
          order.some(id => !byId.has(id))) {
        order = all.map(q => q.id).sort(() => Math.random() - 0.5);
        safeSet(key, order);
      }
      
      setShuffledQuizData(order.map(id => byId.get(id)));
      const firstUnansweredIndex = order.findIndex(id => !valid.has(String((answers || {})[id])));
      setCurrentIndex(firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0);
    }
  // answers is intentionally the mount snapshot used to resume an interrupted quiz.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLang, quizData, assessmentType, bank]); // 監聽語言、數據和類型變化



  // 確保題目正確加載
  if (loading) {
     return <div>{t.loadingQuestions}</div>;
  }
  if (loadError) {
    return <div role="alert">{t.questionLoadFailed}<button onClick={() => setReloadCount(value => value + 1)}>{t.reload}</button></div>;
  }

  if (!shuffledQuizData.length) {
    return <div>📥 加載測評題目中...</div>;
  }

  // 當前題目
  const currentQuestion = shuffledQuizData[currentIndex] || {};
  if (!currentQuestion.id) {
    return <div>⚠️ 加載題目時發生錯誤，請重新整理</div>;
  }

  // 計算進度百分比
  const progress = Math.round(((currentIndex + 1) / shuffledQuizData.length) * 100);

  // 處理選擇答案的邏輯
  const handleAnswer = (answer) => {
    if (!currentQuestion.id) return;
    const updatedAnswers = { ...answers, [currentQuestion.id]: answer || '未知' };
    setAnswers(updatedAnswers);
    safeSet(STORAGE_KEYS.QUIZ_ANSWERS, updatedAnswers);
    if (bank) safeSet(bankAnswerCacheKey(getContext()), updatedAnswers);
    void queueAnswerCheckpoint(currentQuestion.id, answer);

    // 如果還有下一題，則跳到下一題
    if (currentIndex < shuffledQuizData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // 返回上一題
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // 提交測評並跳轉
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const valid = new Set(['0','1','2']);
    const missingIndex = shuffledQuizData.findIndex(q => !valid.has(String((answers || {})[q.id])));

    if (missingIndex >= 0) {
      alert(t.incompleteAlert || '您还有未完成的题目，请先完成再提交。');
      setCurrentIndex(missingIndex);
      setIsSubmitting(false);
      return;
    }
    computeAndSaveTimeTaken();
    // Final checkpoint contains the complete local answer set. Navigation is
    // allowed even while offline because the same snapshot remains queued.
    const checkpointSaved = await queueAnswerSnapshot(answers);
    const submissionContext = resolveSubmissionContext({
      userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}),
      urlParams: getCommonParams(),
      activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
      sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}),
    });
    const params = new URLSearchParams({
      defaultLang: submissionContext.defaultLang || defaultLang,
      checkpointSaved: checkpointSaved ? '1' : '0',
    });
    addMetadataToParams(params, submissionContext);
    [
      ['useruuid', submissionContext.useruuid],
      ['evaluation_uuid', submissionContext.evaluation_uuid],
      ['companyCode', submissionContext.companyCode],
      ['Job_NID', submissionContext.Job_NID],
      ['User_NID', submissionContext.User_NID],
      ['codeType', submissionContext.codeType],
      ['evaluation_type', submissionContext.evaluation_type],
    ].forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`/submit?${params.toString()}`);
  };

  // 根據答案設定選中的按鈕樣式
  const getButtonClass = (option) => (answers[currentQuestion.id] === option ? 'btn-selected' : 'btn-default');

  return (
    <div className="assessment-quiz">
      {/* 進度條 */}
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>

      {/* 題目 */}
      <div className="question-card">
        <h2>
          {t.quizTitle}: {t.questionPrefix} {currentIndex + 1} {t.ofTotal} {shuffledQuizData.length}
        </h2>
        <p className="question-text">{currentQuestion.question || t.noQuestion}</p>

        {/* 選項按鈕 */}
        <div className="options">
          <button className={getButtonClass('2')} onClick={() => handleAnswer('2')}>
            {t.optionYes2}
          </button>
          <button className={getButtonClass('1')} onClick={() => handleAnswer('1')}>
            {t.optionMaybe2}
          </button>
          <button className={getButtonClass('0')} onClick={() => handleAnswer('0')}>
            {t.optionNo2}
          </button>
        </div>
      </div>

      {/* 上一題按鈕 */}
      {currentIndex > 0 && (
        <button className="previous-button" onClick={handlePrevious}>
          {t.previousButton}
        </button>
      )}

      {/* 完成提交按鈕 */}
      {currentIndex === shuffledQuizData.length - 1 && (
        <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
          {t.submitButton}
        </button>
      )}
    </div>
  );
}

export default AssessmentQuiz;
